import React, { useState, useEffect } from "react";
import { Create } from "@refinedev/mui";
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Chip,
  InputAdornment,
  Paper,
  IconButton,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import {
  IResourceComponentsProps,
  useNavigation,
  useTranslate,
  useNotification,
} from "@refinedev/core";
import {
  PersonAdd,
  Email,
  Phone,
  Lock,
  Business,
  PhotoCamera,
  Check,
  ArrowForward,
  ArrowBack,
  Send,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import axiosInstance from "../../api/axios-config";
import { Controller } from "react-hook-form";
import { useAuth } from "../../contexts/auth-context";
import { PhoneNumberInput } from "../../components/common/PhoneInput";
import { isValidPhoneNumber } from "libphonenumber-js";

export const UserCreate: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const { goBack } = useNavigation();
  const { open } = useNotification();
  
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm();

  const [currentUserCompany, setCurrentUserCompany] = useState<any>(null);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<"ADMIN" | "MODERATOR" | null>(null);

  const steps = [
    {
      label: 'Informations personnelles',
      icon: <PersonAdd />,
      fields: ['fullname', 'email', 'phone']
    },
    {
      label: 'Sécurité',
      icon: <Lock />,
      fields: ['password', 'confirmPassword']
    },
    {
      label: 'Organisation',
      icon: <Business />,
      fields: ['entrepriseId', 'role']
    }
  ];

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const { data: result } = await axiosInstance.get('/auth/me');
        setUserRole(result.role);
        
        if (result.entrepriseId) {
          const { data: companyData } = await axiosInstance.get(
            `/company/${result.entrepriseId}`
          );
          setCurrentUserCompany(companyData);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    const fetchAllCompanies = async () => {
      try {
        const { data } = await axiosInstance.get("/company");
        setAllCompanies(data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchUserInfo();
    if (userRole === "ADMIN") {
      fetchAllCompanies();
    }
  }, [userRole]);

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  // Using libphonenumber-js for phone validation instead of regex
  // const isValidPhoneNumber is now imported from libphonenumber-js

  const checkUserExists = async (value: string, type: "email" | "phone") => {
    try {
      const response = await axiosInstance.get('/users/check', {
        params: { [type]: value }
      });
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  const handleNext = async () => {
    const fieldsToValidate = steps[activeStep].fields;
    const isValid = await trigger(fieldsToValidate);
    
    if (isValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);

    try {
      const { confirmPassword, ...submitData } = data;

      // Clean up phone field - if empty, don't send it
      if (submitData.phone === '' || submitData.phone === null) {
        delete submitData.phone;
      }

      let dataFormatted = {
        ...submitData,
        entrepriseId: userRole === "ADMIN"
          ? submitData.entrepriseId?.id || null
          : currentUserCompany?.id || null,
      };

      await axiosInstance.post('/users/create-user', dataFormatted);
      
      open?.({
        type: "success",
        message: "Utilisateur créé avec succès !",
        description: "Un email a été envoyé avec les instructions de connexion.",
      });
      
      goBack();
    } catch (error: any) {
      open?.({
        type: "error",
        message: "Erreur lors de la création",
        description: error.response?.data?.message || "Une erreur est survenue",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Prévisualisation locale
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload vers S3
    try {
      const extension = file.name.split('.').pop();
      const { data } = await axiosInstance.get('/aws/s3-signed-url/put', {
        params: { folder: "profiles", extension }
      });

      await fetch(data.url, { method: "PUT", body: file });
      setValue("profilePicture", data.path);
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="avatar-upload">
                <IconButton
                  color="primary"
                  component="span"
                  sx={{ p: 0 }}
                >
                  <Avatar
                    src={imagePreview || watch("profilePicture")}
                    sx={{
                      width: 120,
                      height: 120,
                      cursor: 'pointer',
                      border: `4px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    <PhotoCamera sx={{ fontSize: 40 }} />
                  </Avatar>
                </IconButton>
              </label>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Cliquez pour ajouter une photo
              </Typography>
            </Box>

            <TextField
              {...register("fullname", {
                required: "Le nom est requis",
              })}
              error={!!errors?.fullname}
              helperText={errors?.fullname?.message as string}
              fullWidth
              label="Nom complet"
              placeholder="Ex: Jean Dupont"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonAdd />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              {...register("email", {
                required: "L'email est requis",
                validate: {
                  validFormat: (value) =>
                    isValidEmail(value) || "Format d'email invalide",
                  emailNotExists: async (value) => {
                    if (!isValidEmail(value)) return true;
                    const exists = await checkUserExists(value, "email");
                    return !exists || "Cet email est déjà utilisé";
                  },
                },
              })}
              error={!!errors?.email}
              helperText={errors?.email?.message as string}
              fullWidth
              type="email"
              label="Email"
              placeholder="Ex: jean.dupont@email.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <Controller
              name="phone"
              control={control}
              rules={{
                validate: {
                  validFormat: (value) =>
                    !value || isValidPhoneNumber(value) || "Format invalide",
                  phoneNotExists: async (value) => {
                    if (!value || !isValidPhoneNumber(value)) return true;
                    const exists = await checkUserExists(value, "phone");
                    return !exists || "Ce numéro est déjà utilisé";
                  },
                },
              }}
              render={({ field, fieldState }) => (
                <PhoneNumberInput
                  {...field}
                  label="Téléphone (optionnel)"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                  margin="normal"
                />
              )}
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <Alert severity="info">
              Le mot de passe doit contenir au moins 8 caractères. 
              Un email sera envoyé à l'utilisateur avec ses identifiants.
            </Alert>

            <TextField
              {...register("password", {
                required: "Le mot de passe est requis",
                minLength: {
                  value: 8,
                  message: "Le mot de passe doit contenir au moins 8 caractères",
                },
              })}
              error={!!errors?.password}
              helperText={errors?.password?.message as string}
              fullWidth
              type={showPassword ? "text" : "password"}
              label="Mot de passe"
              placeholder="••••••••"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              {...register("confirmPassword", {
                required: "Veuillez confirmer le mot de passe",
                validate: (value) => {
                  const password = watch("password");
                  return value === password || "Les mots de passe ne correspondent pas";
                },
              })}
              error={!!errors?.confirmPassword}
              helperText={errors?.confirmPassword?.message as string}
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              label="Confirmer le mot de passe"
              placeholder="••••••••"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            {userRole === "MODERATOR" && currentUserCompany && (
              <>
                <Alert severity="info">
                  L'utilisateur sera automatiquement ajouté à votre organisation.
                </Alert>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Business color="primary" />
                    <Box>
                      <Typography variant="subtitle2">Organisation</Typography>
                      <Typography variant="h6">{currentUserCompany.nom}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </>
            )}

            {userRole === "ADMIN" && (
              <>
                <Controller
                  control={control}
                  name="entrepriseId"
                  rules={{
                    required: "Veuillez sélectionner une entreprise",
                  }}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      label="Entreprise"
                      error={!!errors?.entrepriseId}
                      helperText={errors?.entrepriseId?.message as string}
                      value={field.value || ''}
                      onChange={(e) => {
                        const selectedCompany = allCompanies.find(c => c.id === e.target.value);
                        field.onChange(selectedCompany);
                      }}
                    >
                      {allCompanies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.nom}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />

                <TextField
                  {...register("role", {
                    required: "Le rôle est requis",
                  })}
                  select
                  fullWidth
                  defaultValue="USER"
                  label="Rôle"
                  error={!!errors?.role}
                  helperText={errors?.role?.message as string}
                >
                  <MenuItem value="USER">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip label="USER" size="small" />
                      <Typography variant="caption">Utilisateur standard</Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="MODERATOR">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip label="MODERATOR" size="small" color="primary" />
                      <Typography variant="caption">Peut gérer son organisation</Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="ADMIN">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip label="ADMIN" size="small" color="secondary" />
                      <Typography variant="caption">Accès complet</Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="SAFEE_MODERATOR">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip label="SAFEE_MODERATOR" size="small" color="warning" />
                      <Typography variant="caption">Modérateur Safee</Typography>
                    </Stack>
                  </MenuItem>
                </TextField>
              </>
            )}
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Create
      title="Ajouter un utilisateur"
      breadcrumb={false}
      wrapperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Progression
              </Typography>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: activeStep >= index ? 'primary.main' : 'grey.400',
                          }}
                        >
                          {activeStep > index ? <Check /> : step.icon}
                        </Avatar>
                      )}
                    >
                      {step.label}
                    </StepLabel>
                    <StepContent>
                      <Typography variant="caption" color="text.secondary">
                        {index === 0 && "Nom, email et téléphone"}
                        {index === 1 && "Mot de passe sécurisé"}
                        {index === 2 && "Organisation et permissions"}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Typography variant="h5" gutterBottom sx={{ mb: 4 }}>
                  {steps[activeStep].label}
                </Typography>

                {renderStepContent(activeStep)}

                <Divider sx={{ my: 4 }} />

                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                  >
                    Retour
                  </Button>
                  
                  {activeStep === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                      }}
                    >
                      Créer l'utilisateur
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowForward />}
                    >
                      Suivant
                    </Button>
                  )}
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Create>
  );
};